/**
 * ActiveUsersWidget — "Active now" horizontal strip on Dashboard,
 * showing recently-active users' avatars, click → their profile.
 * Also emits presence beats (throttled by backend) so the caller
 * appears in the widget for others.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, User as UserIcon } from "lucide-react";

const BEAT_INTERVAL_MS = 90_000;      // ~1.5 min — server throttles to 60s anyway
const REFRESH_INTERVAL_MS = 60_000;    // refresh presence list every 60 s
const WINDOW_MINUTES = 15;

export default function ActiveUsersWidget() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  // Presence beat (only when logged in).
  useEffect(() => {
    if (!user) return;
    const beat = () => { http.post("/presence/beat").catch(() => {}); };
    beat();
    const t = setInterval(beat, BEAT_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user]);

  // Poll presence list.
  useEffect(() => {
    let alive = true;
    const load = () => {
      http.get(`/presence/active?minutes=${WINDOW_MINUTES}&limit=24`)
        .then((r) => { if (alive) setItems(r.data.items || []); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="wofy-card mt-6" data-testid="active-users-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
          <div className="overline">ACTIVE NOW · LAST {WINDOW_MINUTES} MIN</div>
        </div>
        <span className="text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>{items.length}</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-1 -mx-1 px-1" data-testid="active-users-strip">
        {items.map((u) => {
          const avatar = u.avatar_url || u.profile_picture;
          const initial = (u.name || u.handle || "?").trim().slice(0, 1).toUpperCase();
          return (
            <Link
              key={u.id}
              to={u.handle ? `/u/${u.handle}` : "#"}
              data-testid={`active-user-${u.id}`}
              className="shrink-0 flex flex-col items-center gap-1.5 w-16 group"
              title={u.name}
            >
              <div className="relative">
                {avatar ? (
                  <img
                    src={avatar}
                    alt={u.name}
                    className="w-14 h-14 rounded-full object-cover border-2 transition group-hover:scale-105"
                    style={{ borderColor: "var(--wofy-primary)" }}
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center font-display text-lg border-2 transition group-hover:scale-105"
                    style={{ borderColor: "var(--wofy-primary)", background: "var(--wofy-surface)" }}
                  >
                    {initial}
                  </div>
                )}
                <span
                  className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2"
                  style={{ background: "#22c55e", borderColor: "#fff" }}
                  data-testid={`active-user-dot-${u.id}`}
                  aria-label="online"
                />
              </div>
              <div className="text-[10px] font-mono truncate max-w-[64px] text-center" style={{ color: "var(--wofy-text-soft)" }}>
                @{u.handle || "user"}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
